import React, { useState, useEffect } from 'react';
import {
  AppLayout,
  TopNavigation,
  SideNavigation,
  ContentLayout,
  Header,
  SpaceBetween,
  Badge
} from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';
import DocumentUpload from './components/DocumentUpload';
import DocumentList from './components/DocumentList';
import DocumentViewer from './components/DocumentViewer';
import ValidationView from './components/ValidationView';
import SummaryGroup from './components/SummaryGroup';
import Settings from './components/Settings';
import SignIn from './components/SignIn';
import { dynamodb, config, configureAWS, areCredentialsExpired, refreshCredentials, setTokenExpiredHandler } from './aws-config';

// Ensure Cloudscape components are properly initialized
if (typeof window !== 'undefined') {
  window.awsuiPlugins = window.awsuiPlugins || { all: [] };
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeHref, setActiveHref] = useState(window.location.pathname || '/signin');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [validationQueueCount, setValidationQueueCount] = useState(0);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Set up global token expiration handler
  useEffect(() => {
    setTokenExpiredHandler(() => {
      console.log('Global token expiration detected, signing out...');
      alert('Your session has expired. Please sign in again.');
      handleSignOut();
    });
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const storedCreds = localStorage.getItem('awsCredentials');
      const storedUser = localStorage.getItem('userInfo');
      
      if (storedCreds && storedUser) {
        try {
          const creds = JSON.parse(storedCreds);
          
          // Check if credentials are expired
          if (areCredentialsExpired()) {
            console.log('Credentials expired, attempting refresh...');
            try {
              const refreshedCreds = await refreshCredentials();
              // Configure with refreshed credentials
              configureAWS(refreshedCreds);
              console.log('Credentials refreshed successfully');
            } catch (refreshError) {
              console.error('Failed to refresh credentials:', refreshError);
              // If refresh fails, clear everything and force re-login
              localStorage.removeItem('awsCredentials');
              localStorage.removeItem('cognitoTokens');
              localStorage.removeItem('userInfo');
              setIsCheckingSession(false);
              return;
            }
          } else {
            // Configure AWS SDK with existing credentials
            configureAWS(creds);
          }
          
          setCurrentUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
          setActiveHref('/upload-document');
        } catch (error) {
          console.error('Session restoration failed:', error);
          // Clear stored data on any error
          localStorage.removeItem('awsCredentials');
          localStorage.removeItem('cognitoTokens');
          localStorage.removeItem('userInfo');
        }
      }
      
      // Mark session check as complete
      setIsCheckingSession(false);
    };
    
    checkExistingSession();
  }, []);

  // Auto-refresh credentials before expiry
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const refreshInterval = setInterval(async () => {
      if (areCredentialsExpired()) {
        try {
          console.log('Auto-refreshing credentials...');
          await refreshCredentials();
        } catch (error) {
          console.error('Auto-refresh failed:', error);
          // Force sign out if refresh fails
          alert('Your session has expired. Please sign in again.');
          handleSignOut();
        }
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Route mapping - converts between URL paths and page titles
  const routes = {
    '/signin': 'SignIn',
    '/upload-document': 'Upload Document',
    '/document-list': 'Document List',
    '/summary-group': 'Summary Group',
    '/validation-queue': 'Validation Queue',
    '/submission-details': 'Submission Details',
    '/settings': 'Settings'
  };

  // Reverse mapping for navigation
  const pathByTitle = Object.entries(routes).reduce((acc, [path, title]) => {
    acc[title] = path;
    return acc;
  }, {});

  // Sync activeHref with browser URL
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      // Only update if it's a valid route
      if (routes[path]) {
        setActiveHref(path);
      } else {
        // Default to upload-document for unknown routes
        setActiveHref('/upload-document');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL and document title when activeHref changes
  useEffect(() => {
    if (window.location.pathname !== activeHref) {
      window.history.pushState({}, '', activeHref);
    }
    // Update browser tab title
    const pageTitle = routes[activeHref] || 'Form Processor';
    document.title = `${pageTitle} - Form Processor`;
  }, [activeHref]);

  // Load validation queue count
  useEffect(() => {
    if (isAuthenticated) {
      loadValidationQueueCount();
      // Refresh count every 30 seconds
      const interval = setInterval(loadValidationQueueCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadValidationQueueCount = async () => {
    try {
      const result = await dynamodb.scan({ 
        TableName: config.submissionsTable,
        FilterExpression: 'processingStatus = :status',
        ExpressionAttributeValues: {
          ':status': 'COMPLETE'
        }
      }).promise();

      const confidenceThreshold = parseFloat(localStorage.getItem('confidenceThreshold') || '85');
      
      const needsValidation = (result.Items || []).filter(item => {
        const confidence = typeof item.confidence === 'number' 
          ? item.confidence 
          : (item.confidence ? parseFloat(item.confidence) : 0);
        const businessRulesScore = typeof item.businessRulesScore === 'number'
          ? item.businessRulesScore
          : (item.businessRulesScore ? parseFloat(item.businessRulesScore) : 100);
        
        return confidence < confidenceThreshold || item.requiresQC || businessRulesScore < 100;
      });

      setValidationQueueCount(needsValidation.length);
    } catch (error) {
      console.error('Error loading validation queue count:', error);
      
      // Check if it's an expired token error
      if (error.code === 'ExpiredTokenException' || error.code === 'ExpiredToken') {
        console.log('Token expired, forcing sign out');
        alert('Your session has expired. Please sign in again.');
        handleSignOut();
      }
    }
  };

  const handleSignIn = (credentials, user) => {
    configureAWS(credentials);
    setCurrentUser(user);
    setIsAuthenticated(true);
    setActiveHref('/upload-document');
  };

  const handleSignOut = () => {
    // Clear all stored data
    localStorage.removeItem('awsCredentials');
    localStorage.removeItem('cognitoTokens');
    localStorage.removeItem('userInfo');
    
    setIsAuthenticated(false);
    setCurrentUser(null);
    setActiveHref('/signin');
    setIsCheckingSession(false);
  };

  // Show loading state while checking session
  if (isCheckingSession) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '18px' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <SignIn onSignIn={handleSignIn} />;
  }

  const navigationItems = [
    { type: 'link', text: 'Upload Document', href: '/upload-document' },
    { type: 'link', text: 'Document List', href: '/document-list' },
    { type: 'link', text: 'Summary Group', href: '/summary-group' },
    { 
      type: 'link', 
      text: (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Validation Queue
          {validationQueueCount > 0 && (
            <Badge color="red">{validationQueueCount}</Badge>
          )}
        </span>
      ),
      href: '/validation-queue' 
    },
    { type: 'divider' },
    { type: 'link', text: 'Settings', href: '/settings' }
  ];

  const renderContent = () => {
    switch (activeHref) {
      case '/upload-document':
        return <DocumentUpload />;
      case '/document-list':
        return <DocumentList onSelectDocument={(doc) => {
          setSelectedDocument(doc);
          setActiveHref('/submission-details');
        }} />;
      case '/summary-group':
        return <SummaryGroup />;
      case '/validation-queue':
        return <ValidationView onValidationComplete={loadValidationQueueCount} />;
      case '/submission-details':
        return <DocumentViewer document={selectedDocument} />;
      case '/settings':
        return <Settings />;
      default:
        return <DocumentUpload />;
    }
  };

  const getPageTitle = () => {
    return routes[activeHref] || 'Form Processor';
  };

  return (
    <div>
      <TopNavigation
        identity={{
          href: '#',
          title: 'Form Processor',
          onFollow: (e) => {
            e.preventDefault();
            setActiveHref('/upload-document');
          }
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: currentUser?.name || currentUser?.username || 'User',
            description: currentUser?.email || '',
            iconName: 'user-profile',
            items: [
              {
                id: 'signout',
                text: 'Sign Out'
              }
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'signout') {
                handleSignOut();
              }
            }
          }
        ]}
      />
      <AppLayout
        navigation={
          <SideNavigation
            activeHref={activeHref}
            onFollow={(event) => {
              if (!event.detail.external) {
                event.preventDefault();
                setActiveHref(event.detail.href);
              }
            }}
            items={navigationItems}
          />
        }
        content={
          <ContentLayout
            header={
              <Header variant="h1">
                {getPageTitle()}
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              {renderContent()}
            </SpaceBetween>
          </ContentLayout>
        }
        toolsHide
      />
    </div>
  );
}

export default App;